import { apiClient } from '@/lib/api/api-client';

export interface McpConnection {
  id: string;
  name: string;
  description?: string;
  status: string;
  settings?: Record<string, any>;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  apiKeys?: McpApiKey[];
  _count?: {
    apiKeys: number;
    syncLogs: number;
  };
}

export interface McpApiKey {
  id: string;
  name: string;
  description?: string;
  key?: string; // Only returned on creation
  keyPrefix: string;
  scopes: string[];
  lastUsedAt?: string;
  lastUsedIp?: string;
  expiresAt?: string;
  createdAt: string;
}

export interface CreateMcpConnectionDto {
  name: string;
  description?: string;
  settings?: Record<string, any>;
}

export interface UpdateMcpConnectionDto {
  name?: string;
  description?: string;
  settings?: Record<string, any>;
}

export interface CreateApiKeyDto {
  name: string;
  description?: string;
  scopes?: string[];
  expiresAt?: string;
  connectionId?: string;
}

export interface McpCapabilities {
  protocolVersion: string;
  capabilities: {
    tools: { listChanged: boolean };
    resources: { subscribe: boolean; listChanged: boolean };
    prompts: { listChanged: boolean };
    logging: Record<string, any>;
  };
  serverInfo: {
    name: string;
    version: string;
    description: string;
  };
  transports: string[];
  authentication: {
    type: string;
    headerName: string;
  };
}

export interface McpTool {
  name: string;
  description: string;
  inputSchema: {
    type: string;
    properties: Record<string, any>;
    required?: string[];
  };
}

export interface McpResource {
  uri: string;
  name: string;
  description?: string;
  mimeType?: string;
}

class McpService {
  private baseUrl = '/integrations/mcp';

  // Connection management
  async createConnection(data: CreateMcpConnectionDto): Promise<McpConnection> {
    return apiClient<McpConnection>(`${this.baseUrl}/connection`, {
      method: 'POST',
      body: data,
    });
  }

  async getConnection(): Promise<McpConnection> {
    return apiClient<McpConnection>(`${this.baseUrl}/connection`);
  }

  async updateConnection(id: string, data: UpdateMcpConnectionDto): Promise<McpConnection> {
    return apiClient<McpConnection>(`${this.baseUrl}/connection/${id}`, {
      method: 'PUT',
      body: data,
    });
  }

  async deleteConnection(id: string): Promise<{ success: boolean }> {
    return apiClient<{ success: boolean }>(`${this.baseUrl}/connection/${id}`, {
      method: 'DELETE',
    });
  }

  // API Key management
  async createApiKey(data: CreateApiKeyDto): Promise<McpApiKey & { message: string }> {
    return apiClient<McpApiKey & { message: string }>(`${this.baseUrl}/api-keys`, {
      method: 'POST',
      body: data,
    });
  }

  async listApiKeys(): Promise<McpApiKey[]> {
    return apiClient<McpApiKey[]>(`${this.baseUrl}/api-keys`);
  }

  async revokeApiKey(id: string): Promise<{ success: boolean }> {
    return apiClient<{ success: boolean }>(`${this.baseUrl}/api-keys/${id}`, {
      method: 'DELETE',
    });
  }

  // MCP information
  async getCapabilities(): Promise<McpCapabilities> {
    return apiClient<McpCapabilities>(`${this.baseUrl}/capabilities`);
  }

  async listTools(): Promise<{ tools: McpTool[] }> {
    return apiClient<{ tools: McpTool[] }>(`${this.baseUrl}/tools`);
  }

  async listResources(): Promise<{ resources: McpResource[] }> {
    return apiClient<{ resources: McpResource[] }>(`${this.baseUrl}/resources`);
  }

  // Metrics
  async getMetrics(hours?: number): Promise<any> {
    const params = hours ? `?hours=${hours}` : '';
    return apiClient<any>(`${this.baseUrl}/metrics${params}`);
  }

  async getRealTimeMetrics(): Promise<{
    activeConnections: number;
    requestsLastMinute: number;
    averageResponseTime: number;
    errorRate: number;
  }> {
    return apiClient<{
      activeConnections: number;
      requestsLastMinute: number;
      averageResponseTime: number;
      errorRate: number;
    }>(`${this.baseUrl}/metrics/real-time`);
  }

  async getErrorDistribution(hours?: number): Promise<Array<{
    errorCode: number;
    count: number;
    percentage: number;
  }>> {
    const params = hours ? `?hours=${hours}` : '';
    return apiClient<Array<{
      errorCode: number;
      count: number;
      percentage: number;
    }>>(`${this.baseUrl}/metrics/errors${params}`);
  }
}

export const mcpService = new McpService();